import React, { useMemo, Fragment, useState } from 'react'
import moment from 'moment'
import cn from 'classnames'

import { Card, makeStyles, Typography, Menu, MenuItem } from '@material-ui/core'

import { getMissionType, getReadableStatus } from 'Containers/Campaigns/utils'
import { addAuthParamsToCampaignBuilderUrl } from 'Containers/Country/Page/Pipeline/PipelineView/utils.js'
import { PATHS } from 'Config/Routing/paths'
import {
  CAMPAIGN_INTERNAL_STATUSES,
  STATUS_CAMPAIGN_ENUM_ACTIVE,
  STATUS_CAMPAIGN_ENUM_ARCHIVE,
  STATUS_CAMPAIGN_ENUM_COMPLETED,
  STATUS_CAMPAIGN_ENUM_DRAFT,
  CAMPAIGN_ENUM_TYPES,
  CAMPAIGN_SUBTYPES_ENUM,
} from '../../../../shared/constants'
import LinearProgressBar from '../../../LinearProgressBar'
import { GenericButton } from '../../../Buttons/GenericButton'
import { styles } from './newStyles'
import { getReadableInternalStatusV1 } from '../../../StatusLabel'
import H2HIcon from '../../../../Assets/Images/icons/h2hIcon'
import D2DIcon from '../../../../Assets/Images/icons/d2dIcon'
import DMIcon from '../../../../Assets/Images/icons/dmIcon'
import { DeleteIcon } from '../../../../Assets/Images/icons/DeleteIconV2'
import CopyIcon from '../../../../Assets/Images/icons/copy'
import { MoreVertIconUnfilled } from '../../../../Assets/Images/icons/MoreVertIconUnfilled'
import { getCampaignBuilderPageUrl } from '../../../../Containers/Country/Page/Pipeline/PipelineView/utils'
import { CardWrapper } from './CardWrapper'
import { SelfServeSteps } from './SelfServeSteps'
import {
  getCampaignStartDate,
  shouldDisplayEndDate,
  getLastStepReached,
} from './utils'
import StyledModal from '../../../Modal/StyledModal'
import classNames from 'classnames'
import useSmallScreen from './useSmallScreen'
import { authorization } from '../../../../Module/Auth/Auth'
import AimIcon from '../../../../Assets/Images/icons/aimIcon'

const useStyles = makeStyles(styles)

export const NewCampaignCard = ({
  campaignSimpleData,
  campaignCalculatedData = {},
  campaignOverviewHref,
  campaignId,
  handleDeleteCampaign,
  handleDuplicateCampaign,
  clientId,
  isSelfServeEnabled,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const {
    id,
    name,
    type,
    subtype,
    status,
    endDate: endDateProp,
    selfServeData,
    currencySign,
    scanDataEnabled,
  } = campaignSimpleData

  const smallScreen = useSmallScreen()

  const internalStatus = selfServeData.length
    ? selfServeData[0].internalStatus
    : null

  const lastActiveStep = selfServeData.length
    ? selfServeData[0].lastActiveStep
    : null

  const startDate = useMemo(() => getCampaignStartDate(campaignSimpleData), [
    campaignSimpleData,
  ])
  const displayEndDate = useMemo(
    () => shouldDisplayEndDate(campaignSimpleData),
    [campaignSimpleData],
  )

  const shouldDisplayReviewBooked =
    campaignSimpleData.reviewMeetingDate &&
    moment(campaignSimpleData.reviewMeetingDate).isAfter(moment()) &&
    (internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT ||
      internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW)

  const endDate = useMemo(() => {
    if (campaignSimpleData.isSelfServe) {
      return endDateProp ? moment(endDateProp).format('YYYY-MM-DD') : ''
    } else {
      return endDateProp ? moment(endDateProp).format('YYYY-MM-DD') : null
    }
  }, [endDateProp])

  const {
    progressScore,
    flyersDistributed = 0,
    // costs = { hardCosts: 0 },
    totalMissions,
    metrics = [],
    scans = 0,
    scansCVR,
    mailPieces,
  } = campaignCalculatedData

  const classes = useStyles()
  const primaryMetric = metrics.filter(metric => metric.isPrimary)[0]
  const isSelfServeCampaignDraftOrReview = useMemo(
    () =>
      internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT ||
      internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW,
    [internalStatus],
  )

  const typeIcon = {
    [CAMPAIGN_ENUM_TYPES.LETTERBOX]: (
      <D2DIcon fill="rgba(39, 55, 84, 1)" width="20" />
    ),
    [CAMPAIGN_ENUM_TYPES.HANDTOHAND]: (
      <H2HIcon fill="rgba(39, 55, 84, 1)" width="20" />
    ),
    [CAMPAIGN_ENUM_TYPES.DIRECTMAIL]: (
      <DMIcon fill="rgba(39, 55, 84, 1)" width="20" />
    ),
    [CAMPAIGN_ENUM_TYPES.INSERTS]: (
      <AimIcon color="rgba(39, 55, 84, 1)" width="24" />
    ),
  }

  const readableStatus = shouldDisplayReviewBooked
    ? 'Review Booked'
    : getReadableInternalStatusV1(internalStatus) || getReadableStatus(status)

  const isDM = type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL && isSelfServeEnabled
  const isADM = isDM && subtype === CAMPAIGN_SUBTYPES_ENUM.ADM
  const isAIM = type === CAMPAIGN_ENUM_TYPES.INSERTS

  const shouldDisplayProgressBar = useMemo(() => {
    if (type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL) {
      return typeof progressScore === 'number'
    } else {
      return !!totalMissions
    }
  }, [type, totalMissions, progressScore])

  const nonClickableCard = useMemo(() => {
    if (!campaignOverviewHref) {
      return true
    }

    if (
      type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL ||
      type === CAMPAIGN_ENUM_TYPES.INSERTS
    ) {
      return [
        CAMPAIGN_INTERNAL_STATUSES.DRAFT,
        CAMPAIGN_INTERNAL_STATUSES.REJECTED,
      ].includes(internalStatus)
    }

    return [
      CAMPAIGN_INTERNAL_STATUSES.DRAFT,
      CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW,
      CAMPAIGN_INTERNAL_STATUSES.UPLOAD_CODES,
      CAMPAIGN_INTERNAL_STATUSES.UPLOAD_CODES_DECLINED,
      CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF,
      CAMPAIGN_INTERNAL_STATUSES.PENDING_DIGITAL_PROOF,
      CAMPAIGN_INTERNAL_STATUSES.PAYMENT,
      CAMPAIGN_INTERNAL_STATUSES.REJECTED,
    ].includes(internalStatus)
  }, [campaignOverviewHref, internalStatus, type])

  const isNotStatusDraftOrActive =
    internalStatus !== CAMPAIGN_INTERNAL_STATUSES.ACTIVE ||
    internalStatus !== CAMPAIGN_INTERNAL_STATUSES.DRAFT

  const statusColors = useMemo(() => {
    const actualStatus =
      status === 'COMPLETED' ? status : internalStatus || status

    if (shouldDisplayReviewBooked) {
      return {
        background: '#E6E8FF',
        color: '#11182C',
      }
    }

    switch (actualStatus) {
      case 'Review Booked':
        return {
          background: '#E6E8FF',
          color: '#11182C',
        }
      case STATUS_CAMPAIGN_ENUM_ACTIVE:
        return {
          color: 'rgba(0, 150, 6, 1)',
          background: 'rgba(0, 150, 6, 0.12)',
        }
      case STATUS_CAMPAIGN_ENUM_ARCHIVE:
        return { color: '#C4C4C4', background: '#C4C4C4' }
      case STATUS_CAMPAIGN_ENUM_DRAFT:
        return {
          color: 'rgba(130, 95, 6, 1)',
          background: 'rgba(255, 245, 0, 0.31)',
        }
      case STATUS_CAMPAIGN_ENUM_COMPLETED:
        return {
          color: 'rgba(9, 112, 187, 1)',
          background: 'rgba(30, 160, 250, 0.15)',
        }
      case CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW:
        return {
          color: 'rgba(83, 5, 186, 1)',
          background: 'rgba(217, 217, 255, 0.8)',
        }
      case CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW:
      case CAMPAIGN_INTERNAL_STATUSES.UPLOAD_CODES:
      case CAMPAIGN_INTERNAL_STATUSES.UPLOAD_CODES_DECLINED:
      case CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF:
      case CAMPAIGN_INTERNAL_STATUSES.PENDING_DIGITAL_PROOF:
      case CAMPAIGN_INTERNAL_STATUSES.PAYMENT:
        return {
          color: 'rgba(172, 84, 3, 1)',
          background: 'rgba(228, 120, 0, 0.14)',
        }
      case CAMPAIGN_INTERNAL_STATUSES.REJECTED:
        return {
          color: 'rgba(179, 15, 89, 1)',
          background: 'rgba(179, 15, 89, 0.12)',
        }
      default:
        return {
          color: 'rgba(130, 95, 6, 1)',
          background: 'rgba(255, 245, 0, 0.31)',
        }
    }
  }, [readableStatus, status])

  const selfServeStepIndex = useMemo(() => {
    if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW) {
      return 4
    }
    if (type === CAMPAIGN_ENUM_TYPES.UNCERTAIN) {
      return -1
    }

    switch (lastActiveStep) {
      case 'home':
        return 0
      case 'audience':
      case 'segments':
        return 1
      case 'design':
        return 2
      case 'details':
        return 3
      case 'submit':
        if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT) {
          return 4
        }

        return 4
      default:
        return 0
    }
  }, [lastActiveStep, internalStatus])

  const resumeUrl = useMemo(() => {
    const campaignBuilderPageUrl = getCampaignBuilderPageUrl({
      campaignId,
      clientId,
      lastActiveStep: getLastStepReached(lastActiveStep, type),
      channel: type,
      subtype: subtype,
    })
    return addAuthParamsToCampaignBuilderUrl(campaignBuilderPageUrl, clientId)
  }, [campaignId, clientId, lastActiveStep, type])

  const handleOpenModal = () => {
    setIsOpenDeleteModal(true)
    setAnchorEl(null)
  }

  const handleDelete = async () => {
    await handleDeleteCampaign(campaignId)
  }

  const handleDuplicate = async () => {
    setAnchorEl(null)
    if (handleDuplicateCampaign) {
      await handleDuplicateCampaign(campaignId)
    }
  }

  const isEddmCampaign = subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM

  return (
    <Fragment>
      <StyledModal
        isOpen={isOpenDeleteModal}
        title="Are you sure you want to delete this campaign?"
        text={
          <span>
            This action cannot be undone. Campaign
            <br /> <strong>{name}</strong> will be deleted.
          </span>
        }
        onClose={() => setIsOpenDeleteModal(false)}
        onConfirm={handleDelete}
        confirmLabel="Delete"
      />
      <CardWrapper isClickable={!nonClickableCard} href={campaignOverviewHref}>
        <Card
          key={`${name}-${id}`}
          className={cn(
            classes.clientCampaignCardWrapper,
            {
              [classes.disableCardClick]: nonClickableCard,
            },
            'clientCampaignCardWrapper',
          )}
          cypress_id={'clientCampaignCard'}
          onClick={
            internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW
              ? () => {
                  if (!authorization.isLogged()) {
                    window.location.href = '/errors?type=auth'
                  } else {
                    window.location.replace(resumeUrl)
                  }
                }
              : undefined
          }
        >
          <div className={cn(classes.container, 'makeStyles-container')}>
            <div className={cn(classes.cardSection, 'makeStyles-cardSection')}>
              <Typography
                className={classes.campaignName}
                cypress_id={'campaignNameInCard'}
              >
                {name}
              </Typography>
              {smallScreen &&
                internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT && (
                  <div
                    onClick={handleOpenModal}
                    className={classes.deleteButton}
                  >
                    <DeleteIcon />
                  </div>
                )}
            </div>
            <div
              className={cn(classes.cardSection, 'makeStyles-cardSection')}
              cypress_id={'cardSection'}
              style={{
                borderBottom:
                  isDM &&
                  internalStatus === CAMPAIGN_INTERNAL_STATUSES.ACTIVE &&
                  '0px',
              }}
            >
              <div
                className={cn(
                  classes.campaignDataSection,
                  'makeStyles-campaignDataSection',
                )}
              >
                <div className={classes.campaignDataFirst}>
                  <div
                    className={cn(classes.status, 'makeStyles-status')}
                    style={{
                      color: statusColors.color,
                      backgroundColor: statusColors.background,
                    }}
                    cypress_id={'campaignStatusInCard'}
                  >
                    {readableStatus}
                  </div>
                  <div
                    className={classes.campaignType}
                    cypress_id={'campaignTypeInCard'}
                  >
                    {typeIcon[type]}
                    <span className={classes.campaignTypeLabel}>
                      {getMissionType(type, subtype)}
                    </span>
                  </div>
                </div>
                <div
                  className={cn(classes.campaignDataSecond, {
                    [classes.spaceAround]: displayEndDate,
                  })}
                >
                  <div className={classNames(classes.date, 'makeStyles-date')}>
                    <span
                      className={classes.dataLabel}
                      cypress_id={'campaignDateTitleInCard'}
                    >
                      Start date
                    </span>
                    <span cypress_id={'campaignDateValueInCard'}>
                      {startDate || '-'}
                    </span>
                  </div>
                  {displayEndDate && (
                    <div
                      className={classNames(classes.date, 'makeStyles-date')}
                    >
                      <span
                        className={classes.dataLabel}
                        cypress_id={'campaignDateTitleInCard'}
                      >
                        {campaignSimpleData.isSelfServe
                          ? 'End date'
                          : 'Expiry Date'}
                      </span>
                      <span cypress_id={'campaignDateValueInCard'}>
                        {endDate || '-'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              className={cn(
                (!isDM ||
                  isADM ||
                  (isDM &&
                    internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT)) &&
                  classes.cardSection,
                {
                  [classes.nonRightBorder]:
                    internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT,
                },
                'makeStyles-cardSection',
              )}
            >
              {isSelfServeEnabled &&
              isSelfServeCampaignDraftOrReview &&
              !isAIM ? (
                <div
                  className={cn(
                    classes.campaignDataSection,
                    'makeStyles-campaignDataSection',
                  )}
                  cypress_id={'OAStepsInCampaignCard'}
                >
                  <SelfServeSteps
                    isEDDM={isDM}
                    isADM={isADM}
                    channel={type}
                    activeTabIndex={selfServeStepIndex}
                    cypress_id={'OAPageInCampaignCard'}
                  />
                </div>
              ) : (
                internalStatus !== CAMPAIGN_INTERNAL_STATUSES.REJECTED &&
                !isAIM && (
                  <div
                    className={classes.metricsSection}
                    cypress_id={'metricTitlesInCard'}
                  >
                    {!isDM && !isAIM && (
                      <React.Fragment>
                        <div className={classes.metric}>
                          <span
                            className={classes.dataLabel}
                            cypress_id={'metricTitleInCard'}
                          >
                            Flyers dist.
                          </span>
                          <span cypress_id={'metricValueInCard'}>
                            {flyersDistributed < 1000
                              ? flyersDistributed
                              : flyersDistributed.toLocaleString('en')}
                          </span>
                        </div>
                        {scanDataEnabled && (
                          <div className={classes.metric}>
                            <span
                              className={classes.dataLabel}
                              cypress_id={'metricTitleInCard'}
                            >
                              Scans
                            </span>
                            <span cypress_id={'metricValueInCard'}>
                              {scans.toLocaleString('en')}
                            </span>
                          </div>
                        )}
                      </React.Fragment>
                    )}

                    {isAIM && (
                      <React.Fragment>
                        <div className={classes.metric}>
                          <span
                            className={classes.dataLabel}
                            cypress_id={'metricTitleInCard'}
                          >
                            Flyers Insert.
                          </span>
                          <span cypress_id={'metricValueInCard'}>
                            {flyersDistributed < 1000
                              ? flyersDistributed
                              : flyersDistributed.toLocaleString('en')}
                          </span>
                        </div>
                        {scanDataEnabled && (
                          <React.Fragment>
                            <div className={classes.metric}>
                              <span
                                className={classes.dataLabel}
                                cypress_id={'metricTitleInCard'}
                              >
                                Scans
                              </span>
                              <span cypress_id={'metricValueInCard'}>
                                {scans.toLocaleString('en')}
                              </span>
                            </div>
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    )}

                    {isDM &&
                      [
                        STATUS_CAMPAIGN_ENUM_ACTIVE,
                        STATUS_CAMPAIGN_ENUM_COMPLETED,
                      ].includes(internalStatus) && (
                        <React.Fragment>
                          <div className={classes.metric}>
                            <span
                              className={classes.dataLabel}
                              cypress_id={'metricTitleInCard'}
                            >
                              Mail pieces
                            </span>
                            <span cypress_id={'metricValueInCard'}>
                              {mailPieces
                                ? mailPieces < 1000
                                  ? mailPieces
                                  : mailPieces.toLocaleString('en')
                                : '-'}
                            </span>
                          </div>

                          <div className={classes.metric}>
                            <span
                              className={classes.dataLabel}
                              cypress_id={'metricTitleInCard'}
                            >
                              Scans
                            </span>
                            <span cypress_id={'metricValueInCard'}>
                              {typeof scans === 'number'
                                ? scans.toLocaleString('en')
                                : '-'}
                            </span>
                          </div>

                          {!isADM && (
                            <div className={classes.metric}>
                              <span
                                className={classes.dataLabel}
                                cypress_id={'metricTitleInCard'}
                              >
                                Scans CVR
                              </span>
                              <span cypress_id={'metricValueInCard'}>
                                {typeof scansCVR === 'number'
                                  ? `${scansCVR} %`
                                  : '-'}
                              </span>
                            </div>
                          )}
                        </React.Fragment>
                      )}
                    {primaryMetric && (!isDM || isADM) && (
                      <Fragment>
                        <div className={classes.metric}>
                          <span className={classes.dataLabel}>
                            {primaryMetric.label || primaryMetric.name}
                          </span>
                          <span>
                            {primaryMetric.metricValue.toLocaleString('en')}
                          </span>
                        </div>
                        <div className={classes.metric}>
                          <span
                            className={classes.dataLabel}
                            cypress_id={'metricTitleInCard'}
                          >
                            CVR
                          </span>
                          <span cypress_id={'metricValueInCard'}>{`${
                            primaryMetric.conversionRate
                              ? primaryMetric.conversionRate >= 100
                                ? 100
                                : primaryMetric.conversionRate.toFixed(2)
                              : 0
                          } %`}</span>
                        </div>
                        <div className={classes.metric}>
                          <span
                            className={classes.dataLabel}
                            cypress_id={'metricTitleInCard'}
                          >
                            CPA
                          </span>
                          <span
                            cypress_id={'metricValueInCard'}
                          >{`${currencySign} ${
                            primaryMetric.costPerMetric
                              ? primaryMetric.costPerMetric.toLocaleString(
                                  'en',
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )
                              : 0
                          }`}</span>
                        </div>
                      </Fragment>
                    )}
                  </div>
                )
              )}
            </div>
            {(!isDM || (isDM && isNotStatusDraftOrActive)) && (
              <div
                className={cn(classes.cardSection, 'makeStyles-cardSection')}
              >
                <div
                  className={cn(
                    classes.campaignDataSection,
                    'makeStyles-campaignDataSection',
                    {
                      [classes.progressWrapper]: ![
                        CAMPAIGN_INTERNAL_STATUSES.REJECTED,
                        CAMPAIGN_INTERNAL_STATUSES.DRAFT,
                        CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW,
                      ].includes(internalStatus),
                      [classes.reviewMessage]: [
                        CAMPAIGN_INTERNAL_STATUSES.REJECTED,
                        CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW,
                      ].includes(internalStatus),
                    },
                  )}
                >
                  {internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW ? (
                    <Typography cypress_id={'campaignDataInCard'}>
                      Reviewing normally takes a few business hours
                    </Typography>
                  ) : internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT ? (
                    <div
                      className={classNames(
                        classes.resumeCampaignContainer,
                        'makeStyles-resumeCampaignContainer',
                      )}
                      cypress_id={'actionBtnInCard'}
                    >
                      {isSelfServeEnabled && !isAIM && (
                        <GenericButton
                          label="Resume"
                          variant="secondary"
                          customStyles={{ width: smallScreen && '100%' }}
                          onClick={() => {
                            if (!authorization.isLogged()) {
                              // eslint-disable-next-line no-console
                              console.log(
                                '## debug, onClick, redirect to /errors?type=auth',
                              )
                              window.location.href = '/errors?type=auth'
                            } else {
                              window.location.href = resumeUrl
                            }
                          }}
                        />
                      )}
                      {!smallScreen && !isAIM && (
                        <MoreVertIconUnfilled
                          className={classes.moreIcon}
                          onClick={event => setAnchorEl(event.currentTarget)}
                        />
                      )}
                      <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                        disableEnforceFocus
                      >
                        {isEddmCampaign && handleDuplicateCampaign && (
                          <MenuItem onClick={handleDuplicate} value="duplicate">
                            <CopyIcon
                              style={{ width: 18, height: 18, fill: '#666' }}
                            />
                            <span style={{ marginLeft: '10px' }}>Duplicate</span>
                          </MenuItem>
                        )}
                        <MenuItem onClick={handleOpenModal} value="delete">
                          <DeleteIcon />
                          <span style={{ marginLeft: '10px' }}>Delete</span>
                        </MenuItem>
                      </Menu>
                    </div>
                  ) : internalStatus === CAMPAIGN_INTERNAL_STATUSES.REJECTED ? (
                    <div
                      className={classNames(
                        classes.resumeCampaignContainer,
                        'makeStyles-resumeCampaignContainer',
                      )}
                      cypress_id={'actionBtnInCard'}
                    >
                      <GenericButton
                        label="Review"
                        href={`${PATHS.dashboard.dashboard}${
                          PATHS.dashboard.home
                        }${PATHS.dashboard.rejected}/${campaignId}`}
                        variant="secondary"
                        customStyles={smallScreen ? { width: '100%' } : {}}
                      />
                    </div>
                  ) : isDM && !isADM ? (
                    isEddmCampaign && handleDuplicateCampaign ? (
                      <div
                        className={classNames(
                          classes.resumeCampaignContainer,
                          'makeStyles-resumeCampaignContainer',
                        )}
                        cypress_id={'actionBtnInCard'}
                        style={{ justifyContent: 'flex-end' }}
                      >
                        {!smallScreen && (
                          <div
                            style={{
                              padding: '10px',
                              cursor: 'pointer',
                              position: 'relative',
                              zIndex: 10,
                            }}
                            onMouseDown={event => {
                              event.preventDefault()
                              event.stopPropagation()
                            }}
                            onClick={event => {
                              event.preventDefault()
                              event.stopPropagation()
                              setAnchorEl(event.currentTarget)
                            }}
                          >
                            <MoreVertIconUnfilled
                              className={classes.moreIcon}
                              style={{ pointerEvents: 'none' }}
                            />
                          </div>
                        )}
                        <Menu
                          id="simple-menu"
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={() => setAnchorEl(null)}
                          disableEnforceFocus
                        >
                          <MenuItem
                            onClick={event => {
                              event.preventDefault()
                              event.stopPropagation()
                              handleDuplicate()
                            }}
                            value="duplicate"
                          >
                            <CopyIcon
                              style={{ width: 18, height: 18, fill: '#666' }}
                            />
                            <span style={{ marginLeft: '10px' }}>Duplicate</span>
                          </MenuItem>
                        </Menu>
                      </div>
                    ) : null
                  ) : shouldDisplayProgressBar ||
                    isAIM ? (
                    <Fragment>
                      <span
                        className={`${classes.dataLabel} , ${
                          classes.progressLabel
                        }`}
                        cypress_id={'campaignDataInCard'}
                      >
                        Campaign progress:
                      </span>
                      <LinearProgressBar
                        variant="determinate"
                        value={
                          progressScore == null
                            ? 0
                            : progressScore >= 100
                            ? 100
                            : progressScore
                        }
                        showValue
                        isLoading={!Object.keys(campaignCalculatedData).length}
                        newDesign
                      />
                    </Fragment>
                  ) : (
                    <Fragment>
                      <span
                        className={classes.dataLabel}
                        cypress_id={'campaignDataInCard'}
                      >
                        Campaign progress:
                      </span>
                      <div>N/A</div>
                    </Fragment>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      </CardWrapper>
    </Fragment>
  )
}
